import { LayoutGroup, motion } from 'motion/react';
import { useRef, useEffect } from 'react';
import { twMerge, twJoin } from 'tailwind-merge';
import TrophyWithPosition from './TrophyWithPosition';

type TrophyCarouselProps = {
  selected: number;
  onChange: (value: number) => void;
};

export default function TrophyCarousel({
  selected,
  onChange,
}: TrophyCarouselProps) {
  const disableCarousel = useRef(false);
  const classNamePosition = ['scale-90', 'scale-100', 'scale-75'];

  useEffect(() => {
    const timeout = setTimeout(
      () => !disableCarousel.current && onChange((selected + 1) % 4 || 1),
      3000,
    );
    return () => {
      clearTimeout(timeout);
    };
  }, [selected, onChange]);

  const onSelect = (position: number) => {
    disableCarousel.current = true;
    onChange(position);
  };

  return (
    <div className="flex gap-2 justify-center">
      <LayoutGroup>
        <TrophyCarouselItem
          position={2}
          className={classNamePosition[0]}
          onSelect={onSelect}
          isSelected={selected === 2}
        />
        <TrophyCarouselItem
          position={1}
          className={classNamePosition[1]}
          onSelect={onSelect}
          isSelected={selected === 1}
        />
        <TrophyCarouselItem
          position={3}
          onSelect={onSelect}
          className={classNamePosition[2]}
          isSelected={selected === 3}
        />
      </LayoutGroup>
    </div>
  );
}

type TrophyCarouselItemProps = {
  className: string;
  position: number;
  isSelected: boolean;
  onSelect: (value: number) => void;
};

const TrophyCarouselItem = ({
  className,
  position,
  isSelected,
  onSelect,
}: TrophyCarouselItemProps) => {
  return (
    <motion.div
      className={twMerge(
        'flex flex-col items-center gap-1 cursor-pointer group transition duration-150 ease-in-out hover:scale-105',
        className,
        isSelected ? 'scale-105' : '',
      )}
      onClick={() => onSelect(position)}
    >
      <TrophyWithPosition position={position} />
      <div className="flex items-center justify-center py-1">
        <motion.div
          className={twJoin(
            'w-2 h-2 rounded-full bg-secondary-content absolute group-hover:bg-secondary',
          )}
        />
        {isSelected && (
          <motion.div
            layoutId="indicator"
            className={twJoin('w-2 h-2 rounded-full bg-secondary absolute')}
          />
        )}
      </div>
    </motion.div>
  );
};
